import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SETTINGS, AI_TIMINGS } from "@/constants/testIds";
import { toast } from "sonner";
import HintIcon from "@/components/HintIcon";
import {
  Cog, Clock, RefreshCw, CalendarClock,
  MessageCircle, PauseCircle, Zap, Timer, Info,
  Pencil, Check, X, Ban, Wrench, Users2, BellOff, Loader2,
} from "lucide-react";
import {
  HMSField, ThresholdField, effSec, fmtDuration,
  SECONDS_PRESETS, SHORT_PRESETS, LONG_PRESETS,
} from "@/components/TimingFields";

const TIMEZONES = ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "UTC"];
const DOWS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/**
 * Section wrapper with per-section Edit/Save/Cancel flow.
 * Children is a render function receiving `{ editing }` so both view and
 * edit modes stay side-by-side and can reuse the same field components.
 */
function EditableSection({ icon: Icon, title, hint, testId, initial, onSave, children }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Sync draft with `initial` whenever the parent re-fetches (view mode only,
  // to avoid stomping on the user's in-flight edits).
  useEffect(() => {
    if (!editing) setDraft(initial);
  }, [initial, editing]);

  const startEdit = () => {
    setDraft(initial);
    setEditing(true);
  };
  const cancel = () => {
    setDraft(initial);
    setEditing(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
      toast.success(`${title} saved`);
    } catch {
      toast.error(`${title} — save failed`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-6 mb-4">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-4 w-4 text-slate-600" />}
        <div className="font-heading font-bold text-base tracking-tight flex-1">{title}</div>
        {hint && <HintIcon hint={hint} />}
        {editing ? (
          <div className="flex items-center gap-2">
            <button
              onClick={cancel}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors duration-150 disabled:opacity-60"
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
              data-testid={testId ? `${testId}-save` : undefined}
            >
              <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        ) : (
          <button
            onClick={startEdit}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors duration-150"
            data-testid={testId ? `${testId}-edit` : undefined}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>
      {children({ editing, draft, setDraft })}
    </div>
  );
}

/** Inline read-only chip for text values (used in view mode). */
function Chip({ children, testId, muted = false }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm ${muted ? "text-slate-500" : "text-slate-800"}`}
    >
      {children}
    </span>
  );
}

function StatusPill({ on, labelOn = "On", labelOff = "Off" }) {
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${
        on
          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
          : "bg-slate-100 border-slate-200 text-slate-500"
      }`}
    >
      {on ? labelOn : labelOff}
    </span>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);   // workspace settings
  const [t, setT] = useState(null);   // AI timing rules
  const [ctrl, setCtrl] = useState(null);  // AI controls — powers digest-inactive UI

  const load = async () => {
    const [settingsResp, timingsResp, ctrlResp] = await Promise.all([
      api.get("/settings"),
      api.get("/ai-timings"),
      api.get("/ai-controls"),
    ]);
    setS(settingsResp.data);
    setT(timingsResp.data);
    setCtrl(ctrlResp.data);
  };
  useEffect(() => { load(); }, []);

  if (!s || !t || !ctrl) return <div className="p-10 text-sm text-slate-500">Loading…</div>;

  const digestsEnabled = ctrl.digests_enabled !== false;

  // ---- Partial save helpers — each section only pushes its own subset. ----
  const patchSettings = async (patch) => {
    const { data } = await api.put("/settings", patch);
    setS(data);
  };
  const patchTimings = async (patch) => {
    const { data } = await api.put("/ai-timings", patch);
    setT(data);
  };

  return (
    <div data-testid={SETTINGS.root} className="p-8 lg:p-12 max-w-[1100px]">
      {/* Header — no global Save button (each section saves itself now) */}
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Admin</div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
          <Cog className="h-7 w-7 text-orange-600" /> Settings
          <HintIcon
            testId="settings-page-hint"
            hint={
              <>
                Master configuration for the platform. Each section is <b>read-only by default</b> — click <i>Edit</i> to change values and <i>Save</i> to persist just that section.
              </>
            }
          />
        </h1>
      </div>

      {/* ================= WORKSPACE ================= */}
      <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-3 mt-2">Workspace</div>

      {/* Timezone */}
      <EditableSection
        icon={Clock}
        title="Timezone"
        testId="settings-section-timezone"
        initial={{ timezone: s.timezone }}
        onSave={(d) => patchSettings({ timezone: d.timezone })}
      >
        {({ editing, draft, setDraft }) => (
          <>
            <p className="text-xs text-slate-600 mb-3">Used for digest schedule and audit-log display.</p>
            {editing ? (
              <select
                data-testid={SETTINGS.timezoneSelect}
                value={draft.timezone}
                onChange={(e) => setDraft({ timezone: e.target.value })}
                className="w-full max-w-xs px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
              >
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            ) : (
              <Chip testId={`${SETTINGS.timezoneSelect}-view`}>{s.timezone}</Chip>
            )}
          </>
        )}
      </EditableSection>

      {/* Master DB auto-sync */}
      <EditableSection
        icon={RefreshCw}
        title="Master DB auto-sync"
        testId="settings-section-sync"
        initial={{
          sync_enabled: s.sync_enabled,
          sync_interval_minutes: s.sync_interval_minutes,
          sync_min_join_date: s.sync_min_join_date || "",
        }}
        onSave={(d) => patchSettings({
          sync_enabled: !!d.sync_enabled,
          sync_interval_minutes: parseInt(d.sync_interval_minutes || "5", 10),
          // v29.4 — send empty string to clear the cutoff, else the trimmed date
          sync_min_join_date: (d.sync_min_join_date || "").trim(),
        })}
      >
        {({ editing, draft, setDraft }) => (
          <>
            <p className="text-xs text-slate-600 mb-3">
              When enabled, PrepHacker Connect polls the <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">MasterDataProvider</code> at this interval to pull new trial users. Real-time signup webhooks can also be posted to <code className="font-mono text-[11px] bg-slate-100 px-1 rounded">/api/webhooks/user-registered</code>.
            </p>
            {editing ? (
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    data-testid={SETTINGS.syncEnabled}
                    type="checkbox"
                    checked={!!draft.sync_enabled}
                    onChange={(e) => setDraft({ ...draft, sync_enabled: e.target.checked })}
                  />
                  Enabled
                </label>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-1">Interval (minutes)</label>
                  <input
                    data-testid={SETTINGS.syncInterval}
                    type="number" min="1" max="1440"
                    value={draft.sync_interval_minutes}
                    onChange={(e) => setDraft({ ...draft, sync_interval_minutes: e.target.value })}
                    className="w-24 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
                  />
                </div>
                <div className="basis-full h-0" />
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-1">
                    Only ingest new users joined on / after
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="settings-sync-min-join-date"
                      type="date"
                      value={(draft.sync_min_join_date || "").slice(0, 10)}
                      onChange={(e) => setDraft({ ...draft, sync_min_join_date: e.target.value })}
                      className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
                    />
                    {draft.sync_min_join_date && (
                      <button
                        type="button"
                        data-testid="settings-sync-min-join-date-clear"
                        onClick={() => setDraft({ ...draft, sync_min_join_date: "" })}
                        className="text-[11px] font-semibold text-slate-500 hover:text-red-600"
                      >Clear</button>
                    )}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-1 max-w-md leading-relaxed">
                    <b>Safeguard for the MariaDB switch-on.</b> Users older than this are <b>NOT</b> inserted on sync — perfect for skipping legacy test junk. <br />
                    Existing users you already imported <b>are always kept in sync</b> (matched by <code className="font-mono">external_id</code>) regardless of this cutoff.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <StatusPill on={!!s.sync_enabled} />
                <Chip>Every {s.sync_interval_minutes} min</Chip>
                {s.sync_min_join_date ? (
                  <Chip>From <b className="ml-1">{new Date(s.sync_min_join_date).toLocaleDateString()}</b></Chip>
                ) : (
                  <span className="text-[10.5px] text-slate-400 italic">No join-date cutoff</span>
                )}
              </div>
            )}
          </>
        )}
      </EditableSection>

      {/* AI Digest schedule — auto-inactivated when digests_enabled=false in AI Control */}
      {digestsEnabled ? (
      <EditableSection
        icon={CalendarClock}
        title="AI Digest schedule"
        testId="settings-section-digest"
        initial={{
          digest_daily_time: s.digest_daily_time,
          digest_weekly_dow: s.digest_weekly_dow,
          digest_weekly_time: s.digest_weekly_time,
        }}
        onSave={(d) => patchSettings({
          digest_daily_time: d.digest_daily_time,
          digest_weekly_dow: parseInt(d.digest_weekly_dow ?? 0, 10),
          digest_weekly_time: d.digest_weekly_time,
        })}
      >
        {({ editing, draft, setDraft }) => (
          <>
            <p className="text-xs text-slate-600 mb-4">
              Times are in the configured timezone ({s.timezone}). Digests are also sent to admin WhatsApp numbers marked &ldquo;receives admin digest&rdquo;.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Daily digest</div>
                {editing ? (
                  <input
                    data-testid={SETTINGS.digestDailyTime}
                    type="time"
                    value={draft.digest_daily_time}
                    onChange={(e) => setDraft({ ...draft, digest_daily_time: e.target.value })}
                    className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
                  />
                ) : (
                  <Chip>{s.digest_daily_time}</Chip>
                )}
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Weekly digest</div>
                {editing ? (
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={SETTINGS.digestWeeklyDow}
                      value={draft.digest_weekly_dow}
                      onChange={(e) => setDraft({ ...draft, digest_weekly_dow: e.target.value })}
                      className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                    >
                      {DOWS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </select>
                    <input
                      data-testid={SETTINGS.digestWeeklyTime}
                      type="time"
                      value={draft.digest_weekly_time}
                      onChange={(e) => setDraft({ ...draft, digest_weekly_time: e.target.value })}
                      className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Chip>{DOWS[s.digest_weekly_dow] || "Monday"}</Chip>
                    <Chip>{s.digest_weekly_time}</Chip>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </EditableSection>
      ) : (
        <div data-testid="settings-section-digest-inactive" className="bg-white border border-dashed border-slate-300 rounded-lg p-6 mb-4 opacity-90">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="h-4 w-4 text-slate-600" />
            <div className="font-heading font-bold text-base tracking-tight flex-1 text-slate-500">AI Digest schedule</div>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-700">
              <Ban className="h-3 w-3" /> Digests turned off
            </span>
          </div>
          <div className="text-xs text-slate-500 leading-relaxed">
            Daily / Weekly digests are <b>disabled</b> in the AI Control Panel, so this schedule has no effect. Turn digests back on from{" "}
            <a href="/ai-controls" className="text-orange-600 font-semibold hover:underline">AI Controls</a> to edit these times.
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Daily</div>
              <div className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500 font-mono tabular">{s.digest_daily_time}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1">Weekly</div>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500">{DOWS[s.digest_weekly_dow] || "Monday"}</div>
                <div className="inline-flex items-center px-2.5 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500 font-mono tabular">{s.digest_weekly_time}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= AI TIMING RULES ================= */}
      <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-3 mt-8 flex items-center gap-2">
        <Timer className="h-3 w-3" /> AI timing rules
      </div>

      {/* Daily AI Message Interval — INACTIVE (Day 1-10 SLA) */}
      <div data-testid="settings-section-day-sla-inactive" className="bg-white border border-dashed border-slate-300 rounded-lg p-6 mb-4 opacity-90">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="h-4 w-4 text-slate-600" />
          <div className="font-heading font-bold text-base tracking-tight flex-1 text-slate-500">Daily AI Message Interval</div>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-700">
            <Ban className="h-3 w-3" /> Not yet coded
          </span>
        </div>
        <div className="text-xs text-slate-500 leading-relaxed">
          These Day 1–10 silence limits are a <b>placeholder feature</b>. The values below are configured in the DB but <b>no scheduling logic currently reads them</b> — changing them has no effect until the silence-nudge cadence is wired up.
        </div>
        <div className="mt-4 grid grid-cols-5 sm:grid-cols-10 gap-2">
          {[1,2,3,4,5,6,7,8,9,10].map((d) => {
            const secs = effSec(t, `day${d}_sla`, (d<=3 ? [8,6,4][d-1] : d<=4 ? 8 : d<=6 ? 12 : 24) * 3600);
            return (
              <div
                key={d}
                className={`text-center px-2 py-2 rounded border ${d <= 3 ? "border-orange-100 bg-orange-50/40" : "border-slate-200 bg-slate-50/60"}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Day {d}</div>
                <div className="text-[11px] font-mono tabular text-slate-500 mt-0.5">{fmtDuration(secs)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reacting to user replies */}
      <EditableSection
        icon={PauseCircle}
        title="Reacting to user replies"
        testId="settings-section-reactions"
        hint={
          <>
            When a user replies, the AI should respond fast — but not spam them with the next scheduled cadence message right away.<br /><br />
            <b>Pause after a reply</b> holds any scheduled cadence step for this long.<br />
            <b>AI reply speed</b> triggers the rapid AI-reply flow (e.g., 10s = AI drafts and sends within 10 seconds).
          </>
        }
        initial={{
          inbound_cooldown_seconds: effSec(t, "inbound_cooldown", 2 * 3600),
          inbound_reply_seconds: effSec(t, "inbound_reply", 30 * 60),
        }}
        onSave={(d) => patchTimings({
          inbound_cooldown_seconds: d.inbound_cooldown_seconds,
          inbound_reply_seconds: d.inbound_reply_seconds,
        })}
      >
        {({ editing, draft, setDraft }) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <HMSField
              testId={AI_TIMINGS.cooldown}
              label="Pause after a reply"
              seconds={editing ? draft.inbound_cooldown_seconds : effSec(t, "inbound_cooldown", 2 * 3600)}
              onChange={(sec) => setDraft({ ...draft, inbound_cooldown_seconds: sec })}
              presets={editing ? SHORT_PRESETS : []}
              readOnly={!editing}
            />
            <HMSField
              testId="ai-timings-inbound-reply-mins"
              label="AI reply speed"
              seconds={editing ? draft.inbound_reply_seconds : effSec(t, "inbound_reply", 30 * 60)}
              onChange={(sec) => setDraft({ ...draft, inbound_reply_seconds: sec })}
              presets={editing ? SECONDS_PRESETS : []}
              readOnly={!editing}
            />
          </div>
        )}
      </EditableSection>

      {/* Hot-lead override */}
      <EditableSection
        icon={Zap}
        title="Hot-lead override — score above which cooldowns are ignored"
        testId="settings-section-hotlead"
        hint="If a user's Conversion Score is at or above this threshold, the AI ignores the cooldown and can send the next cadence step immediately after an inbound reply. Great for closing hot leads."
        initial={{ hot_lead_threshold: t.hot_lead_threshold }}
        onSave={(d) => patchTimings({ hot_lead_threshold: parseInt(d.hot_lead_threshold ?? 70, 10) })}
      >
        {({ editing, draft, setDraft }) => (
          <ThresholdField
            testId={AI_TIMINGS.hotLead}
            label="Score threshold to skip cooldown"
            value={editing ? draft.hot_lead_threshold : t.hot_lead_threshold}
            onChange={(v) => setDraft({ hot_lead_threshold: v })}
            readOnly={!editing}
          />
        )}
      </EditableSection>

      {/* Lifecycle delays (with per-delay on/off switches) */}
      <EditableSection
        icon={Info}
        title="Lifecycle delays"
        testId="settings-section-lifecycle"
        hint={
          <>
            One-time silence buffer between a lifecycle trigger and the next cadence starting. Each buffer has its own <b>On/Off</b> switch — turning a buffer <b>Off</b> makes the corresponding cadence start immediately at the trigger (paid / trial-expired).<br /><br />
            <b>After paid</b> — after a user subscribes, wait this long before the post-subscription cadence begins.<br />
            <b>After trial expired</b> — after the 72-hour trial elapses unpaid, wait this long before the win-back cadence begins.
          </>
        }
        initial={{
          post_conversion_delay_enabled: t.post_conversion_delay_enabled !== false,
          post_conversion_delay_seconds: effSec(t, "post_conversion_delay", 12 * 3600),
          win_back_delay_enabled: t.win_back_delay_enabled !== false,
          win_back_delay_seconds: effSec(t, "win_back_delay", 24 * 3600),
        }}
        onSave={(d) => patchTimings({
          post_conversion_delay_enabled: !!d.post_conversion_delay_enabled,
          post_conversion_delay_seconds: d.post_conversion_delay_seconds,
          win_back_delay_enabled: !!d.win_back_delay_enabled,
          win_back_delay_seconds: d.win_back_delay_seconds,
        })}
      >
        {({ editing, draft, setDraft }) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                key: "post_conversion_delay",
                title: "After paid — retention start delay",
                testId: AI_TIMINGS.postConv,
                defaultSec: 12 * 3600,
              },
              {
                key: "win_back_delay",
                title: "After trial expired — win-back start delay",
                testId: AI_TIMINGS.winBack,
                defaultSec: 24 * 3600,
              },
            ].map(({ key, title: t2, testId, defaultSec }) => {
              const enabledKey = `${key}_enabled`;
              const secondsKey = `${key}_seconds`;
              const currentEnabled = editing ? !!draft[enabledKey] : (t[enabledKey] !== false);
              const currentSecs = editing ? draft[secondsKey] : effSec(t, key, defaultSec);
              return (
                <div key={key} className="border border-slate-100 rounded-md p-3 bg-slate-50/30">
                  <div className="text-xs font-semibold text-slate-700 mb-2">{t2}</div>
                  {editing ? (
                    <>
                      <label className="flex items-center gap-2 text-xs mb-3">
                        <input
                          type="checkbox"
                          data-testid={`${testId}-enabled`}
                          checked={currentEnabled}
                          onChange={(e) => setDraft({ ...draft, [enabledKey]: e.target.checked })}
                        />
                        <span className="font-semibold text-slate-700">Enabled</span>
                        <StatusPill
                          on={currentEnabled}
                          labelOn="Buffer applied"
                          labelOff="No delay — starts at trigger"
                        />
                      </label>
                      <div className={currentEnabled ? "" : "opacity-40 pointer-events-none"}>
                        <HMSField
                          testId={testId}
                          seconds={currentSecs}
                          onChange={(sec) => setDraft({ ...draft, [secondsKey]: sec })}
                          presets={LONG_PRESETS}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusPill
                        on={currentEnabled}
                        labelOn="Buffer applied"
                        labelOff="No delay — starts at trigger"
                      />
                      <HMSField
                        testId={testId}
                        seconds={currentSecs}
                        onChange={() => {}}
                        readOnly
                        disabled={!currentEnabled}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </EditableSection>

      {/* Scheduler heartbeat */}
      <EditableSection
        icon={RefreshCw}
        title="Scheduler heartbeat — how often we check for due messages"
        testId="settings-section-heartbeat"
        hint="How often the background job wakes up to fire due cadence steps and SLAs. Lower = more precise timing, higher = less DB load. Use the seconds override for sub-minute reactivity (min 5s); if left empty, the minutes value applies."
        initial={{
          cadence_tick_minutes: s.cadence_tick_minutes,
          cadence_tick_seconds: s.cadence_tick_seconds ?? "",
        }}
        onSave={(d) => patchSettings({
          cadence_tick_minutes: parseInt(d.cadence_tick_minutes || "5", 10),
          cadence_tick_seconds:
            d.cadence_tick_seconds !== "" && d.cadence_tick_seconds !== null && d.cadence_tick_seconds !== undefined
              ? (parseInt(d.cadence_tick_seconds, 10) || null)
              : null,
        })}
      >
        {({ editing, draft, setDraft }) => (
          editing ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                data-testid={SETTINGS.cadenceTick}
                type="number" min="1" max="60"
                value={draft.cadence_tick_minutes}
                onChange={(e) => setDraft({ ...draft, cadence_tick_minutes: e.target.value })}
                className="w-24 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
              />
              <span className="text-xs text-slate-500">minutes</span>
              <span className="text-slate-300 mx-1 text-xs">·</span>
              <input
                data-testid="settings-cadence-tick-seconds"
                type="number" min="5" max="3600"
                placeholder="optional"
                value={draft.cadence_tick_seconds ?? ""}
                onChange={(e) => setDraft({ ...draft, cadence_tick_seconds: e.target.value })}
                className="w-28 px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
              />
              <span className="text-xs text-slate-500">seconds override</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <Chip>{s.cadence_tick_minutes} min</Chip>
              {s.cadence_tick_seconds ? <Chip>· {s.cadence_tick_seconds}s override</Chip> : null}
            </div>
          )
        )}
      </EditableSection>

      {/* Coordination rules (read-only) */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-4 text-xs text-indigo-900 leading-relaxed mt-2">
        <b>Coordination rules (read-only, applied automatically):</b>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li><b>Rule A</b> — On new trial user sync, the trial-conversion cadence starts <i>immediately at signup</i>; individual step timers control when each message fires.</li>
          <li><b>Rule B</b> — On user inbound, defer scheduled cadence by <i>pause-after-a-reply</i>; jump the queue only if score ≥ <i>hot-lead override</i>.</li>
          <li><b>Rule C</b> — On subscription (paid), switch to <i>Post-Subscription</i> cadence after <i>after-paid delay</i> (skipped when its switch is Off).</li>
          <li><b>Rule D</b> — On trial elapsed without paying, switch to <i>Win-back</i> cadence after <i>after-trial-expired delay</i> (skipped when its switch is Off).</li>
          <li><b>Rule E</b> — On DND, all cadences pause immediately.</li>
        </ul>
      </div>

      {/* v29.9 — Admin Repair Panel */}
      <AdminRepairPanel />
    </div>
  );
}

/** One-page control room for the two idempotent maintenance endpoints. */
function AdminRepairPanel() {
  const [busyKey, setBusyKey] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const run = async (kind) => {
    if (busyKey) return;
    if (!window.confirm(
      kind === "dedupe"
        ? "Merge duplicate users?\n\nClusters by email + phone. Keeps the MariaDB-id row, merges tags/notes, reassigns messages/groups/offers, deletes losers. Idempotent — safe to re-run."
        : "Repair deleted users?\n\nFor every user with subscription_status='deleted' but dnd!=True, sets DND + reason. Also recomputes the Deleted Users group. Idempotent."
    )) return;

    setBusyKey(kind);
    setLastResult(null);
    try {
      const url = kind === "dedupe" ? "/admin/dedupe-users" : "/admin/repair-deleted-users";
      const { data } = await api.post(url);
      setLastResult({ kind, data, at: new Date().toISOString() });
      if (kind === "dedupe") {
        toast.success(`Merged ${data.clusters_merged} cluster(s) · ${data.rows_deleted} row(s) removed`);
      } else {
        toast.success(`Repaired ${data.repaired} user(s) · +${data.added_to_group} added to Deleted Users group`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (e?.message || "Repair failed");
      toast.error(msg);
      setLastResult({ kind, error: msg, at: new Date().toISOString() });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      data-testid="settings-admin-repair-panel"
      className="mt-4 bg-white border border-slate-200 rounded-lg overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Wrench className="h-4 w-4 text-slate-500" />
        <h2 className="font-heading text-lg font-bold tracking-tight">Admin Repair</h2>
        <span className="ml-2 text-[10px] uppercase font-bold tracking-widest text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
          Ops-only
        </span>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Merge Duplicates */}
        <div className="border border-slate-200 rounded-md p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Users2 className="h-4 w-4 text-indigo-600" />
            <span className="font-semibold text-sm">Merge Duplicates</span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Finds users with the same email or phone across multiple rows (e.g. manually-imported + MariaDB-synced). Keeps the MariaDB-id row, merges tags/notes, reassigns messages &amp; group memberships, deletes the losers. <b>Idempotent.</b>
          </p>
          <button
            data-testid="settings-btn-dedupe-users"
            onClick={() => run("dedupe")}
            disabled={!!busyKey}
            className="w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-md"
          >
            {busyKey === "dedupe" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users2 className="h-4 w-4" />}
            {busyKey === "dedupe" ? "Merging…" : "Merge Duplicates"}
          </button>
        </div>

        {/* Repair Deleted Users */}
        <div className="border border-slate-200 rounded-md p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <BellOff className="h-4 w-4 text-red-600" />
            <span className="font-semibold text-sm">Repair Deleted Users</span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            For every user with <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">subscription_status = deleted</code> but <code className="font-mono bg-slate-100 px-1 rounded text-[11px]">dnd ≠ true</code>, sets DND + reason and adds them to the Deleted Users group. Fixes users imported before the v29.8.1 DND-detection fix. <b>Idempotent.</b>
          </p>
          <button
            data-testid="settings-btn-repair-deleted-users"
            onClick={() => run("repair")}
            disabled={!!busyKey}
            className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-md"
          >
            {busyKey === "repair" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="h-4 w-4" />}
            {busyKey === "repair" ? "Repairing…" : "Repair Deleted Users"}
          </button>
        </div>
      </div>

      {/* Last result summary */}
      {lastResult && (
        <div
          data-testid="settings-repair-last-result"
          className={`mx-4 mb-4 p-3 rounded-md border text-xs ${
            lastResult.error
              ? "border-red-200 bg-red-50 text-red-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          <div className="font-semibold mb-1 uppercase tracking-widest text-[10px]">
            Last run · {lastResult.kind === "dedupe" ? "Merge Duplicates" : "Repair Deleted Users"} · {new Date(lastResult.at).toLocaleTimeString()}
          </div>
          {lastResult.error ? (
            <div className="font-mono">{lastResult.error}</div>
          ) : lastResult.kind === "dedupe" ? (
            <div>
              Merged <b>{lastResult.data.clusters_merged}</b> cluster(s) · Deleted <b>{lastResult.data.rows_deleted}</b> row(s)
              {(lastResult.data.details || []).length > 0 && (
                <ul className="mt-2 space-y-1 list-disc ml-5">
                  {lastResult.data.details.slice(0, 5).map((d, i) => (
                    <li key={i}>
                      Kept <b>{d.kept.name || d.kept.email}</b> (ext={d.kept.external_id || "—"});
                      dropped {d.merged_into.length} duplicate(s)
                    </li>
                  ))}
                  {lastResult.data.details.length > 5 && (
                    <li className="italic opacity-70">+ {lastResult.data.details.length - 5} more…</li>
                  )}
                </ul>
              )}
            </div>
          ) : (
            <div>
              Set DND on <b>{lastResult.data.repaired}</b> user(s) · Added <b>{lastResult.data.added_to_group}</b> to Deleted Users group.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
